import React, { useCallback, useRef } from 'react';
import { logError } from 'utils/logger';
import { quoteAPI } from 'modules/quote';
import { useMutation } from '@apollo/client';
import { useEquipmentActions } from 'modules/equipment';
import { EquipmentType } from '@trnsact/trnsact-shared-types';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';

import { TradeInListing } from './TradeInListing';
import { DealBuilderFormValues } from '../../../../types';
import { EMPTY_TRADE_IN_ROW } from '../../../../constants';
import { TradeInEmptyState } from './TradeInEmptyState';
import { useDealBuilderContext } from '../../../../context/DealBuilderContext';
import { dealBuilderApi, UpdateDealBuilderQuoteResponse, UpdateDealBuilderQuoteVariables } from '../../../../api';

export const TradeInSection = () => {
  const { control, getValues, setValue } = useFormContext<DealBuilderFormValues>();
  const { fields, append, remove } = useFieldArray({ control, name: 'pricing.tradeIn' });

  const { onUpsertEquipmentRecord, onDeleteEquipment } = useEquipmentActions();

  const {
    activeEntity,
    setActiveEntity,
    sourceData: { vo },
  } = useDealBuilderContext();

  const vendorOpportunityId = vo?.vendorOpportunityId ?? '';
  const dynamicsVOId = vo?.dynamicsVendorOpportunityId ?? '';

  const [updateDealBuilderQuoteMutation] = useMutation<UpdateDealBuilderQuoteResponse, UpdateDealBuilderQuoteVariables>(
    dealBuilderApi.updateDealBuilderQuote,
    {
      awaitRefetchQueries: true,
      refetchQueries: [quoteAPI.getQuoteCalcById],
    }
  );

  const rowTimersRef = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  const persistQuoteTradeIns = useCallback(async () => {
    const quoteId = activeEntity?.quote?.quoteId;

    if (!quoteId || !vendorOpportunityId) return;

    const rows = getValues('pricing.tradeIn').filter(row => row.equipmentId);

    const result = await updateDealBuilderQuoteMutation({
      variables: {
        quoteId,
        input: {
          vendorOpportunityId,
          equipmentTradeIns: rows.map(row => ({
            equipmentId: row.equipmentId,
            vin: row.serialNumberVin || undefined,
            tradeInDetails: {
              tradeValue: row.tradeInValue ?? 0,
              tradeAmountOwed: row.amountOwed ?? 0,
              actualCashValue: row.actualCashValue ?? 0,
              overUnderAllowance: row.overUnderAllowance ?? 0,
              tradePayoffLienholder: row.payoffLienholder || undefined,
              tradePayoffAccountNumber: row.payoffAccountNumber || undefined,
              tradePayoffGoodThroughDate: row.payoffGoodThroughDate || undefined,
            },
          })),
        },
      },
    });

    const updatedQuote = result.data?.dealBuilder?.updateDealBuilderQuote?.entity;

    if (updatedQuote && activeEntity) {
      setActiveEntity({ ...activeEntity, quote: updatedQuote });
    }
  }, [activeEntity, getValues, updateDealBuilderQuoteMutation, setActiveEntity, vendorOpportunityId]);

  const persistRow = useCallback(
    (rowIndex: number) => {
      clearTimeout(rowTimersRef.current[rowIndex]);

      rowTimersRef.current[rowIndex] = setTimeout(async () => {
        const row = getValues(`pricing.tradeIn.${rowIndex}`);

        logError('Persisting trade-in row', { rowIndex, row });

        const equipmentId = await onUpsertEquipmentRecord({
          quantity: 1,
          make: row.make,
          model: row.model,
          year: row.year,
          mileage: row.mileage,
          type: EquipmentType.TradeIn,
          newUsed: row.newUsed ?? null,
          tradeInValue: row.tradeInValue,
          amountOwedOnTradeIn: row.amountOwed,
          equipmentId: row.equipmentId ?? undefined,
          serialNumberVin: row.serialNumberVin ?? '',
        });

        if (!row.equipmentId) setValue(`pricing.tradeIn.${rowIndex}.equipmentId`, equipmentId);

        await persistQuoteTradeIns();
      }, 1500);
    },
    [getValues, setValue, onUpsertEquipmentRecord, persistQuoteTradeIns]
  );

  const handleAddRow = () => append(EMPTY_TRADE_IN_ROW);

  const handleRemoveRow = (rowIndex: number) => {
    const row = getValues(`pricing.tradeIn.${rowIndex}`);

    if (row.equipmentId) {
      // onDeleteEquipment shows its own confirmation dialog before deleting
      onDeleteEquipment(
        { equipmentId: row.equipmentId, vendorOpportunityId, dynamicsVendorOpportunityId: dynamicsVOId },
        () => {
          remove(rowIndex);
          persistQuoteTradeIns();
        }
      );
    } else {
      remove(rowIndex);
    }
  };

  return (
    <RenderWithCondition condition={!!fields.length} fallBack={<TradeInEmptyState onAdd={handleAddRow} />}>
      <TradeInListing fields={fields} onAddRow={handleAddRow} onFieldBlur={persistRow} onRemoveRow={handleRemoveRow} />
    </RenderWithCondition>
  );
};
